/* S. Studio — theme.js
   Global interactions: loader, header, menu, cursor, particles, reveals, hero split text,
   scramble text, counters, spotlight cards, tabs filter, parallax, tilt, magnetic, page
   transitions, scroll progress, AJAX add-to-cart, toast. */
(function () {
  'use strict';

  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hasGsap = () => typeof window.gsap !== 'undefined';

  /* ---------- Money formatting (mirrors Shopify's formatMoney) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || (window.SStudio && window.SStudio.moneyFormat) || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const value = parseFloat(cents) || 0;
    function fmtDelims(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision; thousands = thousands || ','; decimal = decimal || '.';
      const parts = (number / 100).toFixed(precision).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? dollars + decimal + parts[1] : dollars;
    }
    let out;
    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount_no_decimals': out = fmtDelims(value, 0); break;
      case 'amount_with_comma_separator': out = fmtDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fmtDelims(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = fmtDelims(value, 2, "'", '.'); break;
      default: out = fmtDelims(value, 2);
    }
    return fmt.replace(placeholder, out);
  }
  window.SStudio = window.SStudio || {};
  window.SStudio.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  const toast = document.querySelector('[data-toast]');
  let toastTimer;
  function showToast(text) {
    if (!toast) return;
    toast.querySelector('[data-toast-text]').textContent = text;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3600);
  }
  window.SStudio.toast = showToast;

  /* ---------- Loader (with percentage) ---------- */
  const loader = document.querySelector('.loader');
  if (loader) {
    let seen = false;
    try { seen = !!sessionStorage.getItem('ss-intro'); } catch (e) {}
    if (seen || reduceMotion) {
      loader.remove();
    } else {
      document.body.style.overflow = 'hidden';
      const pct = loader.querySelector('[data-loader-pct]');
      let p = 0; const t0 = performance.now();
      const tickPct = () => { p = Math.min(100, Math.round(((performance.now() - t0) / 1500) * 100)); if (pct) pct.textContent = String(p).padStart(3, '0') + ' %'; if (p < 100) requestAnimationFrame(tickPct); };
      requestAnimationFrame(tickPct);
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        loader.classList.add('is-done');
        document.body.style.overflow = '';
        try { sessionStorage.setItem('ss-intro', '1'); } catch (e) {}
        setTimeout(() => loader.remove(), 1100);
      };
      if (document.readyState === 'complete') setTimeout(finish, 1600);
      else window.addEventListener('load', () => setTimeout(finish, 1100));
      setTimeout(finish, 4000);
    }
  }

  /* ---------- Header + scroll progress + back-to-top ---------- */
  const header = document.querySelector('[data-header]');
  const progress = document.querySelector('.scroll-progress');
  const backTop = document.querySelector('[data-back-top]');
  const onScroll = () => {
    const y = window.scrollY;
    if (header) header.classList.toggle('is-scrolled', y > 24);
    if (progress) { const max = html.scrollHeight - window.innerHeight; progress.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`; }
    if (backTop) backTop.classList.toggle('is-visible', y > window.innerHeight * 0.8);
  };
  const setHeight = () => { if (header) html.style.setProperty('--header-total', header.offsetHeight + 'px'); };
  onScroll(); setHeight();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', setHeight);
  window.addEventListener('load', setHeight);
  if (backTop) backTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));

  /* ---------- Mobile menu ---------- */
  const menuBtn = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  if (menuBtn && menu) {
    menuBtn.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      menuBtn.setAttribute('aria-expanded', String(open));
      menu.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('menu-open', open);
    });
  }

  /* ---------- Custom cursor (dot + ring + glow) ---------- */
  const cursor = document.querySelector('.cursor');
  if (cursor && finePointer && !reduceMotion) {
    const dot = cursor.querySelector('.cursor__dot');
    const ring = cursor.querySelector('.cursor__ring');
    const glow = cursor.querySelector('.cursor__glow');
    let mx = -100, my = -100, rx = -100, ry = -100, gx = -100, gy = -100;
    window.addEventListener('pointermove', (e) => { mx = e.clientX; my = e.clientY; }, { passive: true });
    (function loop() {
      rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18; gx += (mx - gx) * 0.08; gy += (my - gy) * 0.08;
      dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%,-50%)`;
      ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%,-50%)`;
      if (glow) glow.style.transform = `translate(${gx}px, ${gy}px) translate(-50%,-50%)`;
      requestAnimationFrame(loop);
    })();
    document.addEventListener('pointerover', (e) => {
      const t = e.target.closest('a, button, label, [data-tilt], select, input, summary');
      const drag = e.target.closest('[data-watch-3d], model-viewer');
      cursor.classList.toggle('is-hover', !!t && !drag);
      cursor.classList.toggle('is-drag', !!drag);
    });
    document.addEventListener('pointerleave', () => cursor.classList.remove('is-hover', 'is-drag'));
  }

  /* ---------- Gold-dust particles ---------- */
  const pc = document.querySelector('.particles');
  if (pc && !reduceMotion && pc.getContext) {
    const ctx = pc.getContext('2d');
    let w, h, parts = [];
    const resize = () => { w = pc.width = innerWidth; h = pc.height = innerHeight; parts = Array.from({ length: Math.min(90, Math.floor(w * h / 18000)) }, () => spawn(true)); };
    const spawn = (init) => ({ x: Math.random() * w, y: init ? Math.random() * h : h + 10, r: Math.random() * 1.6 + 0.4, vy: -(Math.random() * 0.25 + 0.08), vx: (Math.random() - 0.5) * 0.15, a: Math.random() * 0.5 + 0.15, tw: Math.random() * Math.PI * 2, cyan: Math.random() < 0.15 });
    // Particle colours follow the active colour scheme
    let goldRgb = '230,205,150', cyanRgb = '123,228,238';
    const readColours = () => { const cs = getComputedStyle(html); goldRgb = (cs.getPropertyValue('--accent-rgb') || goldRgb).trim(); cyanRgb = (cs.getPropertyValue('--cyan-rgb') || cyanRgb).trim(); };
    readColours(); document.addEventListener('sstudio:scheme', readColours);
    resize(); window.addEventListener('resize', resize);
    let visible = true; document.addEventListener('visibilitychange', () => { visible = !document.hidden; if (visible) draw(); });
    let raf;
    function draw() {
      if (!visible) { raf = null; return; }
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.y += p.vy; p.x += p.vx; p.tw += 0.02;
        if (p.y < -10) Object.assign(p, spawn(false));
        const alpha = p.a * (0.6 + 0.4 * Math.sin(p.tw));
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.cyan ? cyanRgb : goldRgb},${alpha})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    }
    draw();
  }

  /* ---------- Scroll reveals (+ stagger) ---------- */
  const revealEls = document.querySelectorAll('[data-reveal], [data-stagger]');
  const scrambleEls = [];
  if ('IntersectionObserver' in window && !reduceMotion) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        if (en.target.hasAttribute('data-stagger')) Array.from(en.target.children).forEach((c, i) => { c.style.transitionDelay = (i * 0.07) + 's'; });
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Scramble / decode text on reveal ---------- */
  const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>/\\|=+*';
  function scramble(el) {
    const final = el.dataset.text || el.textContent; el.dataset.text = final;
    const len = final.length; const dur = 900; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      let out = '';
      for (let i = 0; i < len; i++) {
        const ch = final[i];
        if (ch === ' ' || i < p * len) out += ch; else out += glyphs[Math.floor(Math.random() * glyphs.length)];
      }
      el.textContent = out;
      if (p < 1) requestAnimationFrame(step); else el.textContent = final;
    };
    requestAnimationFrame(step);
  }
  if ('IntersectionObserver' in window && !reduceMotion) {
    const sio = new IntersectionObserver((entries) => { entries.forEach((en) => { if (en.isIntersecting) { scramble(en.target); sio.unobserve(en.target); } }); }, { threshold: 0.5 });
    document.querySelectorAll('[data-scramble]').forEach((el) => sio.observe(el));
  }

  /* ---------- Live clock (HUD) ---------- */
  const clocks = document.querySelectorAll('[data-clock]');
  if (clocks.length) {
    const tick = () => { const d = new Date(); const s = [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':'); clocks.forEach((c) => { c.textContent = s; }); };
    tick(); setInterval(tick, 1000);
  }

  /* ---------- Light / dark switch ---------- */
  const schemeCfg = window.SStudioScheme;
  document.querySelectorAll('[data-scheme-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!schemeCfg) return;
      const goLight = html.getAttribute('data-mode') !== 'light';
      // Prefer the store's own scheme when it already matches the requested mode
      const next = goLight ? (schemeCfg.baseMode === 'light' ? schemeCfg.base : schemeCfg.light)
                           : (schemeCfg.baseMode === 'dark' ? schemeCfg.base : schemeCfg.dark);
      if (!reduceMotion) { html.classList.add('scheme-anim'); setTimeout(() => html.classList.remove('scheme-anim'), 600); }
      html.setAttribute('data-scheme', next);
      html.setAttribute('data-mode', goLight ? 'light' : 'dark');
      try { localStorage.setItem('ss-scheme', next); } catch (e) {}
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta && schemeCfg.bgs && schemeCfg.bgs[next]) meta.setAttribute('content', schemeCfg.bgs[next]);
      document.dispatchEvent(new CustomEvent('sstudio:scheme', { detail: { scheme: next } }));
    });
  });

  /* ---------- WhatsApp greeting bubble (once per visit) ---------- */
  const waBubble = document.querySelector('[data-wa-bubble]');
  const waFloat = document.querySelector('[data-wa-float]');
  if (waBubble && waFloat) {
    const seen = () => { try { return !!sessionStorage.getItem('ss-wa-greet'); } catch (e) { return false; } };
    const hide = () => { waBubble.classList.remove('is-visible'); waBubble.setAttribute('aria-hidden', 'true'); try { sessionStorage.setItem('ss-wa-greet', '1'); } catch (e) {} };
    if (!seen()) setTimeout(() => {
      if (seen()) return; // dismissed or chatted in the meantime
      waBubble.classList.add('is-visible'); waBubble.setAttribute('aria-hidden', 'false');
      waFloat.classList.add('is-pinging'); setTimeout(() => waFloat.classList.remove('is-pinging'), 2600);
    }, (parseInt(waBubble.dataset.delay, 10) || 6) * 1000);
    waBubble.querySelector('[data-wa-close]').addEventListener('click', hide);
    waBubble.querySelector('.wa-bubble__cta').addEventListener('click', hide);
    waFloat.addEventListener('click', hide);
  }

  /* ---------- Rotating announcement bar ---------- */
  document.querySelectorAll('[data-announce]').forEach((track) => {
    const msgs = Array.from(track.children);
    if (!msgs.length) return;
    let i = 0; msgs[0].classList.add('is-active');
    if (msgs.length > 1 && !reduceMotion) setInterval(() => { msgs[i].classList.remove('is-active'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-active'); }, 4000);
  });

  /* ---------- Delivery estimate (working days, Pakistan time, Sundays skipped) ---------- */
  document.querySelectorAll('[data-eta]').forEach((el) => {
    const min = parseInt(el.dataset.min || '2', 10), max = parseInt(el.dataset.max || '5', 10);
    const addDays = (n) => { const d = new Date(); let c = 0; while (c < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) c++; } return d; };
    const fmtDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Karachi' });
    el.textContent = min === max ? fmtDate(addDays(min)) : fmtDate(addDays(min)) + ' – ' + fmtDate(addDays(max));
  });

  /* ---------- Hero: split title into lines and reveal ---------- */
  const hero = document.querySelector('[data-hero]');
  if (hero) {
    const title = hero.querySelector('[data-split]');
    function splitLines() {
      if (!title) return;
      const text = title.dataset.text || title.textContent.trim();
      title.dataset.text = text;
      const words = text.split(/\s+/);
      title.innerHTML = '';
      const probeSpans = words.map((w, i) => { const s = document.createElement('span'); s.textContent = (i ? ' ' : '') + w; title.appendChild(s); return s; });
      const lines = []; let cur = []; let lastTop = null;
      probeSpans.forEach((s, i) => {
        const top = s.offsetTop;
        if (lastTop !== null && Math.abs(top - lastTop) > 4) { lines.push(cur); cur = []; }
        cur.push(words[i]); lastTop = top;
      });
      if (cur.length) lines.push(cur);
      title.innerHTML = '';
      lines.forEach((ws, i) => {
        const line = document.createElement('span'); line.className = 'hero__line';
        const inner = document.createElement('span');
        const isLast = i === lines.length - 1;
        if (isLast) { const last = ws.pop(); inner.innerHTML = (ws.length ? ws.join(' ') + ' ' : '') + '<em>' + last + '</em>'; }
        else inner.textContent = ws.join(' ');
        if (title.classList.contains('is-in')) inner.style.transition = 'none';
        else inner.style.transitionDelay = (0.15 + i * 0.12) + 's';
        line.appendChild(inner); title.appendChild(line);
      });
    }
    if (title) {
      title.style.position = 'relative';
      splitLines();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(splitLines);
      let rt; window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(splitLines, 150); });
    }
    const start = () => { requestAnimationFrame(() => { hero.classList.add('is-in'); if (title) title.classList.add('is-in'); }); };
    if (loader && loader.parentNode && !reduceMotion) {
      const obs = new MutationObserver(() => { if (loader.classList.contains('is-done')) { start(); obs.disconnect(); } });
      obs.observe(loader, { attributes: true });
      setTimeout(start, 4200);
    } else start();
    const glow = hero.querySelector('.hero__glow');
    if (finePointer && !reduceMotion && glow) {
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        glow.style.translate = `${x * 40}px ${y * 40}px`;
      }, { passive: true });
    }
  }

  /* ---------- Counters ---------- */
  const counters = document.querySelectorAll('[data-count]');
  if (counters.length) {
    const run = (el) => {
      const target = parseFloat(el.dataset.count) || 0; const dur = 1600; const t0 = performance.now();
      const step = (t) => { const p = Math.min(1, (t - t0) / dur); const e = 1 - Math.pow(1 - p, 4); el.textContent = Math.round(target * e).toLocaleString(); if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    };
    if ('IntersectionObserver' in window && !reduceMotion) {
      const io = new IntersectionObserver((entries) => { entries.forEach((en) => { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } }); }, { threshold: 0.4 });
      counters.forEach((c) => io.observe(c));
    } else counters.forEach((c) => { c.textContent = c.dataset.count; });
  }

  /* ---------- Spotlight (cursor-tracked glow on cards / tiles) ---------- */
  if (finePointer) {
    document.addEventListener('pointermove', (e) => {
      const el = e.target.closest('.card, .tile, .contact-card, .glass[data-spot]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
      el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
    }, { passive: true });
  }

  /* ---------- Tilt cards ---------- */
  if (finePointer && !reduceMotion) {
    document.querySelectorAll('[data-tilt]').forEach((card) => {
      const frame = card.querySelector('.card__frame, .coll-card__frame') || card;
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        frame.style.transform = `perspective(900px) rotateX(${(-y * 7).toFixed(2)}deg) rotateY(${(x * 9).toFixed(2)}deg)`;
        frame.style.transition = 'transform 0.15s ease-out';
      });
      card.addEventListener('pointerleave', () => { frame.style.transform = ''; frame.style.transition = 'transform 0.8s cubic-bezier(.22,1,.36,1)'; });
    });
  }

  /* ---------- Magnetic buttons ---------- */
  if (finePointer && !reduceMotion) {
    document.querySelectorAll('[data-magnetic]').forEach((el) => {
      const inner = el.querySelector('span') || el;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2, y = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${x * 0.18}px, ${y * 0.22}px)`;
        inner.style.transform = `translate(${x * 0.08}px, ${y * 0.1}px)`;
      });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; inner.style.transform = ''; });
    });
  }

  /* ---------- Filter tabs (client-side, by tag) ---------- */
  document.querySelectorAll('[data-tabs]').forEach((tabs) => {
    const grid = document.querySelector(tabs.dataset.tabs);
    if (!grid) return;
    tabs.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-filter]'); if (!btn) return;
      tabs.querySelectorAll('.tab').forEach((t) => t.classList.toggle('is-active', t === btn));
      const f = btn.dataset.filter;
      grid.querySelectorAll('.card').forEach((card, i) => {
        const tags = (card.dataset.tags || '').split(',').map((s) => s.trim());
        const show = f === 'all' || tags.includes(f);
        card.classList.toggle('is-hidden', !show);
        if (show) { card.classList.remove('is-in'); card.style.setProperty('--d', (i % 8) * 0.05 + 's'); requestAnimationFrame(() => card.classList.add('is-in')); }
      });
    });
  });

  /* ---------- Copy-to-clipboard (payment account numbers) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]'); if (!btn) return;
    try { await navigator.clipboard.writeText(btn.dataset.copy); btn.textContent = 'Copied'; showToast('Copied: ' + btn.dataset.copy); }
    catch (err) { showToast(btn.dataset.copy); }
    setTimeout(() => { btn.textContent = 'Copy'; }, 1800);
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]'), plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = (minus || plus).closest('[data-qty]'); const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '0', 10); let v = parseInt(input.value || '1', 10);
    v = plus ? v + 1 : Math.max(min, v - 1);
    input.value = v; input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- AJAX add to cart ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !window.SStudio.routes) return;
    e.preventDefault();
    // "Buy now · Cash on Delivery" adds the item, then goes straight to checkout where COD is chosen.
    const buyNow = !!(e.submitter && e.submitter.hasAttribute('data-buy-now'));
    const btn = buyNow ? e.submitter : form.querySelector('[data-add-to-cart]'); const txt = btn && btn.querySelector('[data-add-text]'); const err = form.querySelector('[data-form-error]');
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    if (err) err.hidden = true;
    try {
      const fd = new FormData(form);
      const res = await fetch(window.SStudio.routes.cartAdd + '.js', { method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag');
      if (buyNow) {
        if (window.SStudio.routes.checkout) { window.location.href = window.SStudio.routes.checkout; return; }
        showToast('Redirecting to checkout…');
      }
      const cart = await fetch(window.SStudio.routes.cartGet, { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
      document.querySelectorAll('[data-cart-count]').forEach((c) => { c.textContent = cart.item_count; c.classList.toggle('is-empty', cart.item_count === 0); c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump'); });
      showToast((data.product_title || 'Item') + ' added to your bag');
      if (btn) { btn.classList.add('is-added'); if (txt) { const old = txt.textContent; txt.textContent = 'Added'; setTimeout(() => { txt.textContent = old; btn.classList.remove('is-added'); }, 1800); } }
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; } else showToast(ex.message);
    } finally { if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; } }
  });

  /* ---------- Page transitions (internal links) ---------- */
  const transition = document.querySelector('.page-transition');
  if (transition && !reduceMotion) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) return; // same page / hash links
      if (url.pathname.startsWith('/checkout') || url.pathname.includes('/account')) return;
      e.preventDefault();
      transition.classList.add('is-active');
      setTimeout(() => { location.href = url.href; }, 650);
    });
    window.addEventListener('pageshow', () => transition.classList.remove('is-active'));
  }

  /* ---------- GSAP scroll-linked motion ---------- */
  function initGsap() {
    if (!hasGsap() || reduceMotion) return;
    if (window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-parallax]').forEach((img) => {
      gsap.fromTo(img, { yPercent: -8 }, { yPercent: 8, ease: 'none', scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
    if (window.ScrollTrigger) {
      // Drive the stage's scroll parallax through a CSS variable, not GSAP's own opacity/transform:
      // the stage's intro reveal also animates those, and GSAP would capture and pin the hidden state.
      const heroEl = document.querySelector('[data-hero]');
      if (heroEl && heroEl.querySelector('[data-hero-stage]')) {
        ScrollTrigger.create({ trigger: heroEl, start: 'top top', end: 'bottom top', scrub: true,
          onUpdate: (self) => heroEl.style.setProperty('--hero-p', self.progress.toFixed(3)) });
      }
      const heroContent = document.querySelector('.hero__content');
      if (heroContent) gsap.to(heroContent, { yPercent: -12, opacity: 0, ease: 'none', scrollTrigger: { trigger: '[data-hero]', start: '30% top', end: 'bottom top', scrub: true } });
      document.querySelectorAll('[data-float]').forEach((el) => {
        gsap.to(el, { y: -30, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
    }
  }
  if (hasGsap()) initGsap(); else window.addEventListener('load', initGsap);

  /* ---------- Shopify model-viewer feature (loads on demand) ---------- */
  function loadModelViewer(cb) {
    if (window.customElements && customElements.get('model-viewer')) { cb && cb(); return; }
    if (window.Shopify && typeof Shopify.loadFeatures === 'function') {
      Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => { if (!errors) cb && cb(); } }]);
    } else {
      const s = document.createElement('script'); s.type = 'module';
      s.src = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
      s.onload = () => cb && cb(); document.head.appendChild(s);
    }
  }
  window.SStudio.loadModelViewer = loadModelViewer;
  if (document.querySelector('model-viewer')) loadModelViewer();
})();
